import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { EuStars } from "../components/EuStars";
import { ArrowLeft, KeyRound, Loader2, MailCheck } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [devLink, setDevLink] = useState(""); // dev-only echo, hidden in prod
    const [contactMsg, setContactMsg] = useState("");

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (!email.trim()) {
            setError("Please enter the email address on your account.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/forgot-password", {
                email: email.trim(),
            });
            if (data?.contact_admin) {
                setContactMsg(data.message);
            } else {
                setSent(true);
                // Dev echo (backend returns dev_reset_url in non-prod)
                if (data?.dev_reset_url) setDevLink(data.dev_reset_url);
            }
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Could not send reset link.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            className="min-h-screen bg-[#F5F0E8] text-[#0D1B3E] flex items-center justify-center p-6"
            data-testid="forgot-password-page"
        >
            <div className="w-full max-w-md">
                <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                    data-testid="forgot-back-link"
                >
                    <ArrowLeft size={14} /> Back to sign-in
                </Link>
                <div className="mt-6 flex items-center gap-3">
                    <EuStars size={26} />
                    <span className="font-display text-xl tracking-tight">EuroLaw Hub</span>
                </div>

                {contactMsg ? (
                    <div className="mt-8" data-testid="forgot-contact-admin">
                        <div className="flex items-center gap-2">
                            <MailCheck size={18} color="#C9A84C" />
                            <span className="label-eyebrow">Personal assistance</span>
                        </div>
                        <h1 className="font-display text-3xl mt-3 tracking-tight">
                            We'll reset it for you.
                        </h1>
                        <p className="mt-4 text-sm text-[#0D1B3E]/75">{contactMsg}</p>
                        <a
                            href="mailto:altay@eurolawhub.com?subject=Password%20reset%20request"
                            className="btn-primary mt-6 inline-flex"
                            data-testid="forgot-contact-email"
                        >
                            Email altay@eurolawhub.com
                        </a>
                    </div>
                ) : !sent ? (
                    <>
                        <div className="mt-8 flex items-center gap-2">
                            <KeyRound size={18} color="#C9A84C" />
                            <span className="label-eyebrow">Reset password</span>
                        </div>
                        <h1 className="font-display text-4xl mt-3 tracking-tight">
                            Forgot your password?
                        </h1>
                        <p className="mt-3 text-sm text-[#0D1B3E]/75">
                            Enter the email on your account and we&apos;ll send a secure,
                            time-limited link (30 minutes) that lets you set a new password.
                        </p>

                        <form onSubmit={submit} className="mt-7 space-y-5">
                            <div>
                                <label className="label-eyebrow">Email address</label>
                                <input
                                    type="email"
                                    className="field mt-2 min-h-[44px]"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    required
                                    data-testid="forgot-email-input"
                                />
                            </div>
                            {error && (
                                <div
                                    className="text-sm text-rose-700"
                                    data-testid="forgot-error"
                                >
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                className="btn-primary w-full min-h-[44px]"
                                disabled={busy}
                                data-testid="forgot-submit"
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                                {busy ? "Sending link…" : "Send reset link"}
                            </button>
                        </form>

                        <p className="mt-6 text-xs text-[#0D1B3E]/55">
                            We&apos;ll never tell you whether an email is registered. For
                            security you&apos;ll see a success message either way.
                        </p>
                    </>
                ) : (
                    <>
                        <div className="mt-10 flex items-center gap-2">
                            <MailCheck size={20} color="#C9A84C" />
                            <span className="label-eyebrow">Check your email</span>
                        </div>
                        <h1 className="font-display text-4xl mt-3 tracking-tight" data-testid="forgot-sent-title">
                            Reset link sent.
                        </h1>
                        <p className="mt-3 text-[#0D1B3E]/75 text-sm">
                            If an account exists for <strong>{email}</strong>, we&apos;ve just
                            issued a secure reset link. Open the email and click the link
                            within 30 minutes. If it expires, request a new one.
                        </p>

                        {devLink && (
                            <div
                                className="mt-6 border border-[#C9A84C]/60 bg-[#C9A84C]/10 p-4 text-sm break-all"
                                data-testid="forgot-dev-link"
                            >
                                <div className="label-eyebrow mb-2">Dev environment: click to open</div>
                                <Link
                                    to={devLink}
                                    className="underline text-[#0D1B3E] hover:text-[#C9A84C]"
                                >
                                    {devLink}
                                </Link>
                            </div>
                        )}

                        <div className="mt-8 flex gap-3">
                            <Link to="/login" className="btn-secondary min-h-[44px]" data-testid="forgot-return-signin">
                                Back to sign-in
                            </Link>
                            <button
                                type="button"
                                onClick={() => { setSent(false); setEmail(""); setDevLink(""); }}
                                className="btn-primary min-h-[44px]"
                                data-testid="forgot-send-another"
                            >
                                Send another
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
